import { supabase } from './supabase.js';
import { toCamel } from './case.js';

/**
 * Create a new partner invite.
 * Uses a SECURITY DEFINER function on the database side to bypass
 * the permission check that direct INSERT would trigger on auth.users
 * (via the FK constraint on inviter_id).
 *
 * Email is optional. Returns the invite with code.
 */
export async function createInvite(email = null) {
  const { data, error } = await supabase.rpc('create_pair_invite', { p_email: email || null });
  if (error) throw error;
  if (!data?.success) {
    throw new Error(translateInviteError(data?.error));
  }
  return toCamel(data);
}

function translateInviteError(code) {
  switch (code) {
    case 'not_authenticated': return 'Du skal være logget ind';
    case 'already_has_pending_invite': return 'Du har allerede en aktiv invitation. Annullér den først.';
    case 'already_paired': return 'Du har allerede en partner';
    case 'code_generation_failed': return 'Kunne ikke generere unik kode — prøv igen';
    default: return 'Kunne ikke oprette invitation: ' + (code || 'ukendt fejl');
  }
}

/**
 * Get the active (pending) invite the current user has sent.
 * Returns null if none.
 *
 * Uses RPC to a SECURITY DEFINER function to bypass permission issues
 * with the FK constraint to auth.users.
 */
export async function fetchMyActiveInvite() {
  const { data, error } = await supabase.rpc('get_my_active_invite');
  if (error) {
    console.error('fetchMyActiveInvite error:', error);
    return null;
  }
  return data ? toCamel(data) : null;
}

/**
 * Cancel an invitation (by inviter).
 */
export async function cancelInvite(inviteId) {
  const { data, error } = await supabase.rpc('cancel_pair_invite', { p_invite_id: inviteId });
  if (error) throw error;
  if (!data?.success) throw new Error(data?.error || 'Cancel failed');
  return data;
}

/**
 * Get invite preview info (for the accept-page before fully accepting).
 * Returns { found, status, inviter_name, inviter_photo } or { found: false }.
 */
export async function getInvitePreview(inviteCode) {
  const { data, error } = await supabase.rpc('get_invite_preview', { p_invite_code: inviteCode });
  if (error) throw error;
  return data;
}

/**
 * Accept an invitation. Must be authenticated.
 * Returns { success, pair_id, partner_id } or { success: false, error }.
 */
export async function acceptInvite(inviteCode) {
  const { data, error } = await supabase.rpc('accept_pair_invite', { p_invite_code: inviteCode });
  if (error) throw error;
  return data;
}

/**
 * Decline an invitation. Must be authenticated.
 */
export async function declineInvite(inviteCode) {
  const { data, error } = await supabase.rpc('decline_pair_invite', { p_invite_code: inviteCode });
  if (error) throw error;
  return data;
}

/**
 * Build the public URL for an invite.
 */
export function buildInviteUrl(code) {
  return `${window.location.origin}/invite/${code}`;
}
